import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { TenantThemeProvider } from './contexts/TenantThemeContext';
import TenantManifestLink from './components/TenantManifestLink';
import InstallPrompt from './components/InstallPrompt';

import AuthPage from './pages/auth/AuthPage';
import AuthCallback from './pages/auth/AuthCallback';
import PlayerApp from './pages/player/PlayerApp';
import AndroidAppDownloadPage from './pages/player/AndroidAppDownloadPage';
import FifaKitPage from './pages/player/FifaKitPage';
import ExploreEarnPage from './pages/player/ExploreEarnPage';
import ExploreSubPage from './pages/player/ExploreSubPage';
import ScorePredictionsPage from './pages/player/ScorePredictionsPage';
import RoulettePage from './pages/player/RoulettePage';
import MarketplacePage from './pages/player/MarketplacePage';
import ReferralPage from './pages/player/ReferralPage';
import PromoCodePage from './pages/player/PromoCodePage';
import BrandProfilePage from './pages/player/BrandProfilePage';
import TasksPage from './pages/player/TasksPage';
import DepositReturnPage from './pages/player/DepositReturnPage';
import AdminLayout from './pages/admin/AdminLayout';
// TenantAuthPage retired — `/<slug>/auth` now uses the shared AuthPage,
// which is tenant-aware via `useTenantTheme()` + `X-Tenant-Slug` header.
import LandingPage from './pages/LandingPage';
import StaffPortalPage from './pages/staff/StaffPortalPage';
import AgentSignupPage from './pages/staff/AgentSignupPage';
import FinancePortalPage from './pages/finance/FinancePortalPage';
import api from './services/api';
import { isAgentsHubHost, isAllowedTenantSegment, isControlPlaneHost, tenantSlugFromHost, tenantSlugFromPathOrHost } from './utils/tenantDomains';
import AgentsHubPage from './pages/hub/AgentsHubPage';

const SlotsOceansLandingV2 = lazy(() => import('./player-v2/pages/landing/SlotsOceansLandingV2'));
const SlotsOceansUIPreview = lazy(() => import('./player-v2/pages/app/SlotsOceansUIPreview'));

const RESERVED_PATHS = ['auth','app','admin','api','staff','staff-portal','agent-portal','agent','finance','survey','deposit-return','download-app','terms','legal','privacy','v2','ui-preview'];
const TERMS_PDF_URL = '/legal/slots-oceans-terms-and-conditions.pdf';
const ADMIN_PORTAL_ROLES = [
  'admin',
  'tenant_admin',
  'moderator',
  'support',
  'staff',
  'support_agent',
  'finance_staff',
  'survey_staff',
];
const AGENT_PORTAL_ROLES = [
  'agent',
  'affiliate_manager',
  'referral_agent',
];

function getTenantHostSlug() {
  return tenantSlugFromHost();
}

function LandingV2Preview() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-[#031427]" aria-label="Loading landing preview" />}>
      <SlotsOceansLandingV2 />
    </Suspense>
  );
}

function PlayerUIPreview() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-[#04111d]" aria-label="Loading player UI preview" />}>
      <SlotsOceansUIPreview />
    </Suspense>
  );
}

function ProtectedRoute({ children, requiredRole, playerOnly = false }) {
  const { user, loading, tenantSlug } = useAuth();
  const location = useLocation();
  const hostTenantSlug = getTenantHostSlug();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B0E14' }}>
        <div className="spinner" />
      </div>
    );
  }

  const pathMatch = location.pathname.match(/^\/([a-z0-9-]+)\//i);
  const slug = tenantSlug || hostTenantSlug || (pathMatch && !RESERVED_PATHS.includes(pathMatch[1]) && isAllowedTenantSegment(pathMatch[1]) ? pathMatch[1] : null);
  const routePrefix = hostTenantSlug ? '' : (slug ? `/${slug}` : '');

  if (!user) {
    if (requiredRole === 'agent') {
      return <Navigate to={`${routePrefix}/agent/login`} replace />;
    }
    if (requiredRole === 'admin') {
      const isChatRoute = /\/admin\/chat(\/|$)/.test(location.pathname);
      const params = new URLSearchParams();
      if (isChatRoute) {
        params.set('staff_chat', '1');
        params.set('next', location.pathname + location.search);
      }
      const suffix = params.toString() ? `?${params.toString()}` : '';
      return <Navigate to={`${routePrefix}/admin/auth${suffix}`} replace />;
    }
    return <Navigate to={`${routePrefix}/auth`} replace />;
  }

  const userTenantSlug = user.tenant_slug || user.tenant?.slug || null;
  if (userTenantSlug && !user.is_master_admin && !hostTenantSlug) {
    const requestedSlug = pathMatch && !RESERVED_PATHS.includes(pathMatch[1]) ? pathMatch[1] : null;
    if (requestedSlug && requestedSlug !== userTenantSlug) {
      const defaultPath = AGENT_PORTAL_ROLES.includes(user.role)
        ? '/agent-portal'
        : (ADMIN_PORTAL_ROLES.includes(user.role) ? '/admin' : '/app');
      const suffix = location.pathname.replace(/^\/[^/]+/, '') || defaultPath;
      return <Navigate to={`/${userTenantSlug}${suffix}`} replace />;
    }
    if (!requestedSlug && (location.pathname.startsWith('/admin') || location.pathname.startsWith('/app'))) {
      return <Navigate to={`/${userTenantSlug}${location.pathname}`} replace />;
    }
  }

  if (requiredRole === 'admin' && !ADMIN_PORTAL_ROLES.includes(user.role)) {
    const destination = AGENT_PORTAL_ROLES.includes(user.role) ? '/agent-portal' : '/app';
    return <Navigate to={`${routePrefix}${destination}`} replace />;
  }

  if (requiredRole === 'agent' && !AGENT_PORTAL_ROLES.includes(user.role)) {
    const destination = ADMIN_PORTAL_ROLES.includes(user.role) ? '/admin' : '/app';
    return <Navigate to={`${routePrefix}${destination}`} replace />;
  }

  if (playerOnly && AGENT_PORTAL_ROLES.includes(user.role)) {
    return <Navigate to={`${routePrefix}/agent-portal`} replace />;
  }

  if (playerOnly && ADMIN_PORTAL_ROLES.includes(user.role)) {
    return <Navigate to={`${routePrefix}/admin`} replace />;
  }

  return children;
}

// Handles bare tenant URL like /morpheus → show branded LandingPage
// (if user already logged in, redirect to their app/admin)
function TenantRootRedirect({ tenantSlugOverride = null }) {
  const params = useParams();
  const tenantSlug = tenantSlugOverride || params.tenantSlug;
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B0E14' }}>
        <div className="spinner" />
      </div>
    );
  }

  if (user) {
    if (AGENT_PORTAL_ROLES.includes(user.role)) {
      return <Navigate to={tenantSlugOverride ? '/agent-portal' : `/${tenantSlug}/agent-portal`} replace />;
    }
    if (tenantSlugOverride) {
      if (ADMIN_PORTAL_ROLES.includes(user.role)) {
        return <Navigate to="/admin" replace />;
      }
      return <Navigate to="/app" replace />;
    }
    if (ADMIN_PORTAL_ROLES.includes(user.role)) {
      return <Navigate to={`/${tenantSlug}/admin`} replace />;
    }
    return <Navigate to={`/${tenantSlug}/app`} replace />;
  }

  // Unauthenticated → show branded landing page (main landing w/ tenant theme)
  return <LandingPage tenantSlugOverride={tenantSlugOverride ? tenantSlug : null} />;
}

function RootEntry() {
  const hostTenant = getTenantHostSlug();
  if (hostTenant) return <TenantRootRedirect tenantSlugOverride={hostTenant} />;
  return <Navigate to="/admin/auth" replace />;
}

function RootAuthEntry() {
  return getTenantHostSlug()
    ? <AuthPage portal="player" />
    : <Navigate to="/admin/auth" replace />;
}

function RootAppEntry({ children }) {
  return getTenantHostSlug()
    ? <ProtectedRoute playerOnly>{children}</ProtectedRoute>
    : <Navigate to="/admin" replace />;
}

function LegacyStaffPortalRedirect() {
  const { tenantSlug } = useParams();
  return <Navigate to={tenantSlug ? `/${tenantSlug}/agent-portal` : '/agent-portal'} replace />;
}

function TermsRedirect() {
  useEffect(() => {
    window.location.replace(TERMS_PDF_URL);
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 text-center" style={{ background: '#03162B', color: '#fff' }}>
      <p>
        Opening{' '}
        <a href={TERMS_PDF_URL} className="underline" style={{ color: '#5CD8FF' }}>
          Slots Oceans Terms &amp; Conditions
        </a>
        ...
      </p>
    </div>
  );
}

function TenantFrontendGate({ children }) {
  const location = useLocation();
  const lookup = tenantSlugFromPathOrHost(location.pathname, window.location.hostname);

  useEffect(() => {
    if (!lookup) return undefined;
    let cancelled = false;

    const resolveFrontend = async () => {
      try {
        const response = await api.get('/api/tenant/current');
        if (cancelled) return;
        const tenant = response.data?.tenant;
        if (!tenant) return;

        const isAdminPath = /(^|\/)admin(\/|$)/.test(location.pathname);
        const isAgentPath = /(^|\/)agent(\/|$)/.test(location.pathname);
        const isAgentPortalPath = /(^|\/)(?:agent|agent-portal|staff-portal)(\/|$)/.test(location.pathname);
        const isLegalPath = /(^|\/)(?:terms|legal|privacy)(\/|$)/.test(location.pathname);
        const portal = tenant.domain_role === 'agent' || isAgentPath ? 'agent' : 'player';
        const frontend = tenant.frontend?.[portal];

        if (tenant.domain_role === 'agent' && !isAgentPortalPath && !isLegalPath) {
          window.location.replace('/agent');
          return;
        }

        if (
          !isAdminPath
          && isAgentPath
          && tenant.domain_role !== 'agent'
          && tenant.agent_portal?.status === 'active'
          && tenant.agent_portal?.url
        ) {
          const target = new URL(tenant.agent_portal.url);
          const pathParts = location.pathname.split('/').filter(Boolean);
          if (pathParts[0] === lookup && !lookup.includes('.')) pathParts.shift();
          target.pathname = `/${pathParts.join('/')}` || '/agent';
          target.search = location.search;
          window.location.replace(target.toString());
          return;
        }

        if (!isAdminPath && frontend?.mode === 'custom' && frontend?.url) {
          const target = new URL(frontend.url, window.location.origin);
          const pathParts = location.pathname.split('/').filter(Boolean);
          if (pathParts[0] === lookup && !lookup.includes('.')) pathParts.shift();
          const routePath = pathParts.length ? `/${pathParts.join('/')}` : '/';
          const basePath = target.pathname.replace(/\/$/, '');
          target.pathname = `${basePath}${routePath === '/' ? '' : routePath}` || '/';
          const currentParams = new URLSearchParams(location.search);
          currentParams.forEach((value, key) => {
            if (!target.searchParams.has(key)) target.searchParams.set(key, value);
          });
          if (target.origin !== window.location.origin || target.pathname !== location.pathname) {
            window.location.replace(target.toString());
            return;
          }
        }

        if (!isAdminPath && tenant.domain_role === 'agent' && location.pathname === '/') {
          window.location.replace('/agent');
        }
      } catch {
        // Route-level auth/theme handling will show the existing tenant error.
      }
    };

    resolveFrontend();
    return () => { cancelled = true; };
  }, [lookup, location.pathname, location.search]);

  return children;
}

function AppRouter() {
  const location = useLocation();
  const firstPathSegment = location.pathname.split('/').filter(Boolean)[0] || '';

  // The Agents Hub owns its subdomain outright; on localhost it answers on
  // a path so it can be developed without DNS.
  const isLocalDevHost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  if (isAgentsHubHost() || (isLocalDevHost && firstPathSegment === 'agents-hub')) {
    return <AgentsHubPage />;
  }

  if (isControlPlaneHost()) {
    const path = location.pathname.replace(/\/+$/, '') || '/';
    const allowed = (
      path === '/'
      || path === '/admin'
      || path.startsWith('/admin/')
      || path === '/auth/callback'
      || path === '/terms'
      || path.startsWith('/legal/')
      || path === '/privacy'
    );
    if (!allowed) return <Navigate to="/admin/auth" replace />;
  }

  if (
    firstPathSegment
    && !RESERVED_PATHS.includes(firstPathSegment)
    && !isAllowedTenantSegment(firstPathSegment)
  ) {
    return <Navigate to="/" replace />;
  }

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Master routes */}
      <Route path="/v2" element={<LandingV2Preview />} />
      <Route path="/ui-preview" element={<PlayerUIPreview />} />
      <Route path="/auth" element={<RootAuthEntry />} />
      <Route path="/admin/auth" element={<AuthPage portal="admin" />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/terms" element={<TermsRedirect />} />
      <Route path="/deposit-return" element={<DepositReturnPage />} />
      <Route path="/download-app" element={<AndroidAppDownloadPage />} />
      <Route path="/app" element={<RootAppEntry><PlayerApp /></RootAppEntry>} />
      <Route path="/app/fifa-kit" element={<RootAppEntry><FifaKitPage /></RootAppEntry>} />
      <Route path="/app/explore" element={<RootAppEntry><ExploreEarnPage /></RootAppEntry>} />
      <Route path="/app/explore/marketplace" element={<RootAppEntry><MarketplacePage /></RootAppEntry>} />
      <Route path="/app/explore/tiers" element={<RootAppEntry><ExploreSubPage section="tiers" /></RootAppEntry>} />
      <Route path="/app/explore/daily-tasks" element={<RootAppEntry><TasksPage kind="daily" /></RootAppEntry>} />
      <Route path="/app/explore/weekly-tasks" element={<RootAppEntry><TasksPage kind="weekly" /></RootAppEntry>} />
      <Route path="/app/explore/referral" element={<RootAppEntry><ReferralPage /></RootAppEntry>} />
      <Route path="/app/explore/promo-code" element={<RootAppEntry><PromoCodePage /></RootAppEntry>} />
      <Route path="/app/explore/roulette" element={<RootAppEntry><RoulettePage /></RootAppEntry>} />
      <Route path="/app/explore/predict-score" element={<RootAppEntry><ScorePredictionsPage /></RootAppEntry>} />
      <Route path="/app/brand" element={<RootAppEntry><BrandProfilePage /></RootAppEntry>} />
      <Route path="/app/*" element={<RootAppEntry><PlayerApp /></RootAppEntry>} />
      <Route path="/admin" element={<ProtectedRoute requiredRole="admin"><AdminLayout /></ProtectedRoute>} />
      <Route path="/admin/*" element={<ProtectedRoute requiredRole="admin"><AdminLayout /></ProtectedRoute>} />
      <Route path="/agent" element={<AgentSignupPage />} />
      <Route path="/agent/signup" element={<AgentSignupPage />} />
      <Route path="/agent/login" element={<AgentSignupPage />} />
      <Route path="/agent/enroll" element={<AgentSignupPage />} />
      <Route path="/agent-portal" element={<ProtectedRoute requiredRole="agent"><StaffPortalPage /></ProtectedRoute>} />
      <Route path="/staff-portal" element={<LegacyStaffPortalRedirect />} />
      <Route path="/finance" element={<FinancePortalPage />} />
      <Route path="/finance/auth" element={<FinancePortalPage />} />
      <Route path="/survey/auth" element={<AuthPage portal="admin" />} />
      <Route path="/survey" element={<ProtectedRoute requiredRole="admin"><AdminLayout /></ProtectedRoute>} />
      <Route path="/survey/*" element={<ProtectedRoute requiredRole="admin"><AdminLayout /></ProtectedRoute>} />
      
      {/* Tenant routes */}
      <Route path="/:tenantSlug/v2" element={<LandingV2Preview />} />
      <Route path="/:tenantSlug/ui-preview" element={<PlayerUIPreview />} />
      <Route path="/:tenantSlug/auth" element={<AuthPage portal="player" />} />
      <Route path="/:tenantSlug/admin/auth" element={<AuthPage portal="admin" />} />
      <Route path="/:tenantSlug/terms" element={<TermsRedirect />} />
      <Route path="/:tenantSlug/deposit-return" element={<DepositReturnPage />} />
      <Route path="/:tenantSlug/download-app" element={<AndroidAppDownloadPage />} />
      <Route path="/:tenantSlug/app" element={<ProtectedRoute playerOnly><PlayerApp /></ProtectedRoute>} />
      <Route path="/:tenantSlug/app/fifa-kit" element={<ProtectedRoute playerOnly><FifaKitPage /></ProtectedRoute>} />
      <Route path="/:tenantSlug/app/explore" element={<ProtectedRoute playerOnly><ExploreEarnPage /></ProtectedRoute>} />
      <Route path="/:tenantSlug/app/explore/marketplace" element={<ProtectedRoute playerOnly><MarketplacePage /></ProtectedRoute>} />
      <Route path="/:tenantSlug/app/explore/tiers" element={<ProtectedRoute playerOnly><ExploreSubPage section="tiers" /></ProtectedRoute>} />
      <Route path="/:tenantSlug/app/explore/daily-tasks" element={<ProtectedRoute playerOnly><TasksPage kind="daily" /></ProtectedRoute>} />
      <Route path="/:tenantSlug/app/explore/weekly-tasks" element={<ProtectedRoute playerOnly><TasksPage kind="weekly" /></ProtectedRoute>} />
      <Route path="/:tenantSlug/app/explore/referral" element={<ProtectedRoute playerOnly><ReferralPage /></ProtectedRoute>} />
      <Route path="/:tenantSlug/app/explore/promo-code" element={<ProtectedRoute playerOnly><PromoCodePage /></ProtectedRoute>} />
      <Route path="/:tenantSlug/app/explore/roulette" element={<ProtectedRoute playerOnly><RoulettePage /></ProtectedRoute>} />
      <Route path="/:tenantSlug/app/explore/predict-score" element={<ProtectedRoute playerOnly><ScorePredictionsPage /></ProtectedRoute>} />
      <Route path="/:tenantSlug/app/brand" element={<ProtectedRoute playerOnly><BrandProfilePage /></ProtectedRoute>} />
      <Route path="/:tenantSlug/admin" element={<ProtectedRoute requiredRole="admin"><AdminLayout /></ProtectedRoute>} />
      <Route path="/:tenantSlug/admin/*" element={<ProtectedRoute requiredRole="admin"><AdminLayout /></ProtectedRoute>} />
      <Route path="/:tenantSlug/agent" element={<AgentSignupPage />} />
      <Route path="/:tenantSlug/agent/signup" element={<AgentSignupPage />} />
      <Route path="/:tenantSlug/agent/login" element={<AgentSignupPage />} />
      <Route path="/:tenantSlug/agent/enroll" element={<AgentSignupPage />} />
      <Route path="/:tenantSlug/agent-portal" element={<ProtectedRoute requiredRole="agent"><StaffPortalPage /></ProtectedRoute>} />
      <Route path="/:tenantSlug/staff-portal" element={<LegacyStaffPortalRedirect />} />
      
      {/* Bare tenant URL: /morpheus → redirect to /morpheus/auth */}
      <Route path="/:tenantSlug" element={<TenantRootRedirect />} />
      
      {/* Root */}
      <Route path="/" element={<RootEntry />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <TenantThemeProvider>
            <TenantManifestLink />
            <TenantFrontendGate><AppRouter /></TenantFrontendGate>
            {!isControlPlaneHost() && <InstallPrompt />}
            {/* Keep short confirmations visible above bottom navigation and
                action bars, especially in the mobile player experience. */}
            <Toaster
              position="top-right"
              duration={2600}
              visibleToasts={2}
              expand={false}
              offset={16}
              mobileOffset={{ top: 12, right: 12, left: 12 }}
            />
          </TenantThemeProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
